class TrueTypeCanvas {
  constructor( canvasId, backgroundImageSrc, textParamsArray, debug = false, downloadImage = "" ) {
    this.canvas = document.getElementById(canvasId);
    this.ctx = this.canvas.getContext("2d");
    this.backgroundImage = new Image();
    this.backgroundImageSrc = backgroundImageSrc;
    this.backgroundImageLoaded = false;
    this.debug = debug;
    this.textParamsArray = textParamsArray;

    this.backgroundImage.onload = () => {
      this.backgroundImageLoaded = true;
      this.updateCanvasText();
    };

    this.backgroundImage.src = this.backgroundImageSrc;

    // Add event listeners for each textInputId in the textParamsArray
    this.textParamsArray.forEach((textParams) => {
      const textInput = document.getElementById(textParams.textInputId);
      if (textInput) {
        textInput.addEventListener("input", () => {
          this.updateCanvasText();
        });
      } else {
        console.error(
          `Input element with id '${textParams.textInputId}' not found.`
        );
      }
    });

    this.downloadImage = document.getElementById(downloadImage);
    if (this.downloadImage) {
      this.downloadImage.addEventListener("click", () => {
        this.downloadCanvasImage();
      });
    }
  }

  async updateCanvasText() {
    if (!this.backgroundImageLoaded) return;

    /* wait until every font used in textParamsArray is ready */
    await this.ensureFontsLoaded();
    
    this.setCanvasSize();
    this.drawImage();

    // Iterate through each textParams object in the array
    this.textParamsArray.forEach((textParams) => {
      const textInput = document.getElementById(textParams.textInputId).value || textParams.default_text;
      const textInputEl = document.getElementById(textParams.textInputId);
      
      let formattedText = this.applyCustomText(textInput, textParams);

      // const personalisationTextArea = document.getElementById('personalisation-text-area');
      // if (personalisationTextArea) {
      //   personalisationTextArea.value = formattedText;
      // }
      
      const boundary = this.calculateBoundary(textParams);
 
      const fontSize = this.resizeFont(formattedText, boundary, textParams);
      const { textX, textY } = this.calculateTextPosition(boundary, textParams.alignment);

      this.setTextStyles(fontSize, textParams);
      this.transformText(textX, textY, textParams);
      this.drawText(formattedText, textParams);
      this.restoreCanvasState();

      if (this.debug) {
        this.drawBoundary(boundary);
      }
    });
  }

  async ensureFontsLoaded() {
    // Cache the promise so we don’t refetch on every keystroke
    if (this._fontsReady) return this._fontsReady;

    // Collect unique font families from textParamsArray
    const families = [
      ...new Set(this.textParamsArray.map(p => p.fontFamily || ''))
    ].filter(f => f.trim() !== '');

    // Build an array of promises, one per family
    const loaders = families.map(font =>
      // The size “10px” is arbitrary – we only care about the family name
      document.fonts.load(`10px "${font}"`)
    );

    // Combine them and store the resulting promise for reuse
    this._fontsReady = Promise.all(loaders)
      .then(() => document.fonts.ready)   // extra safety
      .catch(err => {
        console.warn('Font failed to load:', err);
        /* Optional fallback: continue anyway */
      });

    return this._fontsReady;
  }

  setCanvasSize() {
    this.canvas.width = this.backgroundImage.width;
    this.canvas.height = this.backgroundImage.height;
  }

  drawImage() {
    this.ctx.drawImage(this.backgroundImage, 0, 0);
  }

  calculateBoundary(textParams) {
    return {
      xL: textParams.xL,
      xR: textParams.xR,
      yT: textParams.yT,
      yB: textParams.yB,
    };
  }

  calculateTextPosition(boundary, alignment) {
    let textX;
    switch (alignment) {
      case "left":
        textX = boundary.xL;
        break;
      case "center":
        textX = (boundary.xL + boundary.xR) / 2;
        break;
      case "right":
        textX = boundary.xR;
        break;
      default:
        textX = (boundary.xL + boundary.xR) / 2;
        break;
    }
  
    const textY = (boundary.yT + boundary.yB) / 2;
    return { textX, textY };
  }


  setTextStyles(fontSize, textParams) {

    this.ctx.font = `${fontSize}px ${textParams.fontFamily}`;
    this.ctx.fillStyle = textParams.color;
    this.ctx.textBaseline = "middle";
  
    switch (textParams.alignment) {
      case "left":
        this.ctx.textAlign = "left";
        break;
      case "center":
        this.ctx.textAlign = "center";
        break;
      case "right":
        this.ctx.textAlign = "right";
        break;
      default:
        this.ctx.textAlign = "center"; // Default to center alignment
        break;
    }

    this.ctx.miterLimit = 4;
    this.ctx.lineJoin = 'round';

  }

  transformText(textX, textY, textParams) {
    this.ctx.save();
    this.ctx.translate(textX, textY);
    this.ctx.rotate(((textParams.rotation || 0) * Math.PI) / 180);

    if (textParams.dropShadow) {
      this.ctx.shadowColor = textParams.dropShadowColor;
      this.ctx.shadowOffsetX = textParams.dropShadowOffsetX;
      this.ctx.shadowOffsetY = textParams.dropShadowOffsetY;
      this.ctx.shadowBlur = 2;
    }
  }

  drawText(formattedText, textParams) {
    
    this.ctx.save();
    
    if (textParams.stroke) {
      this.ctx.strokeStyle = textParams.strokeColor || "white";
      this.ctx.lineWidth = textParams.strokeSize || 8;
      this.ctx.strokeText(formattedText, 0, 0);
    }

    this.ctx.restore();
    
    formattedText = this.applyCaseRule(formattedText, textParams);

    this.ctx.fillText(formattedText, 0, 0);
  }

  restoreCanvasState() {
    this.ctx.restore();
  }

  drawBoundary(boundary) {
    this.ctx.strokeStyle = "yellow";
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(
      boundary.xL,
      boundary.yT,
      boundary.xR - boundary.xL,
      boundary.yB - boundary.yT
    );
  }

  applyCaseRule(text, textParams) {
    switch (textParams.caseRule) {
      case "lowercase":
        return text.toLowerCase();
      case "uppercase":
        return text.toUpperCase();
      case "capitalize":
        return text.replace(/\b\w/g, (char) => char.toUpperCase());
      default:
        return text; 
    }
  }

  applyCustomText(text, textParams) {

    let modifiedText = text;
    
    if (textParams.custom_prefix !== "") {
      modifiedText = textParams.custom_prefix + " " + modifiedText;
    }
  
    if (textParams.custom_suffix === "s" || textParams.custom_suffix === "'s") {
      // Check if the user's text ends with 's' (case-insensitive)
      if (!modifiedText.toLowerCase().endsWith("s")) {
        modifiedText = modifiedText + "'s";
      } else {
        modifiedText = modifiedText + "'";
      }
    } else if (textParams.custom_suffix !== "") {
      modifiedText = modifiedText + " " + textParams.custom_suffix;
    }
  
    //return modifiedText;
    return this.applyCaseRule(modifiedText, textParams);
  }

  resizeFont(textInput, boundary, textParams) {
    let fontSize = textParams.defaultFontSize; // Initial font size

    // Calculate the width and height of the text
    this.ctx.font = `${fontSize}px ${textParams.fontFamily}`;
    const textWidth = this.ctx.measureText(textInput).width;
    const textHeight = fontSize; // Assuming text height equals font size

    // Check if text exceeds boundary in width or height
    if ( textWidth > boundary.xR - boundary.xL || textHeight > boundary.yB - boundary.yT ) {
      // Reduce font size to fit text within boundary
      fontSize = Math.min(
        ((boundary.xR - boundary.xL) * fontSize) / textWidth,
        ((boundary.yB - boundary.yT) * fontSize) / textHeight
      );
    }

    // Ensure a minimum font size of 10px
    fontSize = Math.max(fontSize, 10);

    return fontSize;
  }

  downloadCanvasImage(filename = "canvas_image.png") {
    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");

    // Set the href attribute to the data URL of the canvas image
    link.href = this.canvas.toDataURL();

    // Set the download attribute to specify the filename
    link.download = filename;

    // Simulate a click on the anchor element to trigger the download
    link.click();
  }
}